import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Row, Col, Table, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import AppNav from '../../../header/AppNav';
import { PatientServices } from '../../../services/PatientService';

const PatientDetailsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patientDetails, setPatientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPatientDetails = async () => {
      try {
        setLoading(true);
        const response = await PatientServices.getPatientDetails(patientId);
        if (response && response.data) {
          setPatientDetails(response.data);
        } else {
          setError('Patient details not found');
        }
      } catch (error) {
        console.error('Error fetching patient details:', error);
        setError('Failed to load patient details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchPatientDetails();
    }
  }, [patientId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const formatAge = (age) => {
    if (!age) return 'N/A';
    return `${age} years`;
  };

  const renderPrimaryComplaints = () => {
    if (!patientDetails.primaryComplaints || !Array.isArray(patientDetails.primaryComplaints) || patientDetails.primaryComplaints.length === 0) {
      return (
        <tr>
          <td colSpan="2" className="text-center text-muted">No primary complaints recorded</td>
        </tr>
      );
    }

    // Get first two complaints from the array
    const complaintsToShow = patientDetails.primaryComplaints.slice(0, 2);
    
    return complaintsToShow.map((complaint, index) => (
      <tr key={complaint.id || index}>
        <td>{complaint.initialComplaintDetails?.complaint || 'N/A'}</td>
        <td>{formatDate(complaint.createdAt) || formatDate(complaint.date) || 'N/A'}</td>
      </tr>
    ));
  };

  const renderScreeningComplaints = () => {
    if (!patientDetails.complaints) {
      return (
        <tr>
          <td colSpan="6" className="text-center text-muted">No screening complaints recorded</td>
        </tr>
      );
    }

    const complaints = patientDetails.complaints;
    return (
      <tr>
        <td>{complaints.cataractType || 'N/A'}</td>
        <td>{complaints.glaucomaType || 'N/A'}</td>
        <td>{complaints.drType || 'N/A'}</td>
        <td>{complaints.cataractReferredHospital?.hospitalName || 'N/A'}</td>
        <td>{complaints.glaucomaReferredHospital?.hospitalName || 'N/A'}</td>
        <td>{complaints.drReferredHospital?.hospitalName || 'N/A'}</td>
      </tr>
    );
  };

  const handleGoBack = () => {
    navigate('/patients-history');
  };

  if (loading) {
    return (
      <div>
        <AppNav />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner animation="border" role="status" style={{ color: '#083270' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading patient details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <AppNav />
        <div className="container mt-4">
          <Alert variant="danger">
            <h4>Error</h4>
            <p>{error}</p>
            <Button variant="primary" onClick={handleGoBack}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Patients History
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  if (!patientDetails) {
    return (
      <div>
        <AppNav />
        <div className="container mt-4">
          <Alert variant="warning">
            <h4>Patient Not Found</h4>
            <p>The requested patient details could not be found.</p>
            <Button variant="primary" onClick={handleGoBack}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Patients History
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AppNav />
      <div className="container-fluid mt-4" style={{ padding: '0 2rem' }}>
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faUserCircle} size="2x" className="me-3" style={{ color: '#083270' }} />
            <div>
              <h2 className="mb-0" style={{ color: '#083270' }}>Patient Details</h2>
              {/* <p className="text-muted mb-0">Comprehensive patient information</p> */}
            </div>
          </div>
          <Button variant="outline-primary" onClick={handleGoBack}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Patients History
          </Button>
        </div>

        {/* Basic Information Section */}
        <Card className="mb-4" style={{ borderTop: '3px solid #083270' }}>
          <Card.Header className="bg-light">
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0" style={{ color: '#083270' }}>
                  Basic Information
                </h5>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-0">
                  <strong>Patient Name:</strong>
                  <span className="ms-2">{patientDetails.name || 'N/A'}</span>
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-0">
                  <strong>Patient ID: </strong>
                  {patientDetails.patientId || 'N/A'}
                </p>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-0">
                  <strong>Gender: </strong>
                    {patientDetails.gender || 'N/A'}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-0">
                  <strong>Age: </strong>
                  <span className="ms-2">{formatAge(patientDetails.age)}</span>
                </p>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-0">
                  <strong>Mobile Number: </strong>
                  <span className="ms-2">{patientDetails.mobileNo || 'N/A'}</span>
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-0">
                  <strong>Adhar Number: </strong>
                  <span className="ms-2">{patientDetails.idNumber || 'N/A'}</span>
                </p>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-0">
                  <strong>Address:</strong>
                  <span className="ms-2">{patientDetails.address || patientDetails.village || 'N/A'}</span>
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-0">
                  <strong>Asha Name:</strong>
                  <span className="ms-2">{patientDetails.ashaDetails?.name || 'N/A'}</span>
                </p>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-0">
                  <strong>Asha Block:</strong>
                  <span className="ms-2">{patientDetails.ashaBlockDetails?.ashaBlock || 'N/A'}</span>
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-0">
                  <strong>Insurance:</strong>
                  <span className="ms-2">{patientDetails.insurance || 'N/A'}</span>
                </p>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Primary Complaints Section */}
        {/* <Row className="align-items-center">
          <Col>
            <h5 className="mb-0" style={{ color: '#083270' }}>
              Primary Complaints (Mentioned by ASHA)
            </h5>
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          <Col md={12}>
            <Card className="h-100 border-0 shadow-sm" style={{ borderTop: '3px solid #083270' }}>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped bordered hover className="mb-0">
                    <thead className="table-primary">
                      <tr>
                        <th>Complaint</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderPrimaryComplaints()}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row> */}

        {/* Screening Results Section */}
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0" style={{ color: '#083270' }}>
              Screening Results & Referrals
            </h5>
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          <Col md={12}>
            <Card className="h-100 border-0 shadow-sm" style={{ borderTop: '3px solid #083270' }}>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped bordered hover className="mb-0">
                    <thead className="table-danger">
                      <tr>
                        <th>Cataract Result</th>
                        <th>Glaucoma Result</th>
                        <th>DR Result</th>
                        <th>Cataract Hospital</th>
                        <th>Glaucoma Hospital</th>
                        <th>DR Hospital</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderScreeningComplaints()}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PatientDetailsPage;
